import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Box, Menu, MenuItem } from '@mui/material';

import { Icon, Tooltip } from '@sbv-clients/ui-shared';

import { Button } from '../button/button';
import { styled, useTheme } from '../styles/styles';
import Typography from '../typography/typography';
import { NavigationProps, RouteConfigItemType } from './types';

const GAP = 8;

export const NavigationMenu = ({ items, onChangeSubMenu }: NavigationProps) => {
  const theme = useTheme();
  const location = useLocation();

  const containerRef = useRef<HTMLDivElement>(null);

  const measureRef = useRef<HTMLDivElement>(null);

  const [visibleCount, setVisibleCount] = useState(items.length);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  const TabIcon = styled(Icon)<{ isSelected: boolean }>`
    color: ${({ isSelected, theme }) =>
      isSelected ? theme.colors.primary.main : theme.background.darkGrey};
  `;

  const buttonStyle = {
    color: theme.text?.subText2 || '',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '18px',
    letterSpacing: '-0.28px',
    whiteSpace: 'nowrap',
    width: 'fit-content',
    flexShrink: 0,
    gap: '4px',
    border: `0 solid ${theme.colors.primary.light}`,
    '&:hover': { backgroundColor: theme.colors.primary.light },
  };

  const selectedButtonStyle = {
    ...buttonStyle,
    border: `1px solid ${theme.colors.primary.light}`,
    backgroundColor: theme.colors.primary.light,
    color: theme.background.darkGrey3,
    fontWeight: 500,
  };

  const isSelected = (item: RouteConfigItemType) => {
    const pathname = location.pathname;

    const isMainRoute = item.path === 'main';

    return (
      pathname.includes(item.path) ||
      (pathname.endsWith('accruals') && isMainRoute)
    );
  };

  const renderButton = (item: RouteConfigItemType, index: any) => {
    const selected = isSelected(item);

    return (
      <Tooltip key={index} title={item.label}>
        <span>
          <Button
            key={item.path}
            onClick={() => onChangeSubMenu?.(item.path)}
            sx={selected ? selectedButtonStyle : buttonStyle}
          >
            {item.icon && <TabIcon isSelected={selected} name={item.icon} />}
            {(!item.icon || selected) && (
              <Typography
                sx={{
                  color: selected
                    ? theme.colors.primary.main
                    : theme.background.darkGrey,
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                {item.label}
              </Typography>
            )}
          </Button>
        </span>
      </Tooltip>
    );
  };

  const selectedItem = items.find(isSelected);

  const visibleItems = useMemo(() => {
    const baseVisible = items.slice(0, visibleCount);

    if (!selectedItem) {
      return baseVisible;
    }

    const selectedAlreadyVisible = baseVisible.some(
      (item) => item.path === selectedItem.path
    );

    if (selectedAlreadyVisible) {
      return baseVisible;
    }

    return [
      ...baseVisible.slice(0, Math.max(visibleCount - 1, 0)),
      selectedItem,
    ];
  }, [items, visibleCount, selectedItem]);

  const hiddenItems = useMemo(() => {
    return items.filter(
      (item) => !visibleItems.some((visible) => visible.path === item.path)
    );
  }, [items, visibleItems]);

  const measurementItems = useMemo(() => {
    const result = [...items];

    if (!selectedItem) {
      return result;
    }

    const selectedIndex = result.findIndex(
      (item) => item.path === selectedItem.path
    );

    if (selectedIndex >= visibleCount) {
      result.splice(selectedIndex, 1);

      result.splice(Math.max(visibleCount - 1, 0), 0, selectedItem);
    }

    return result;
  }, [items, selectedItem, visibleCount]);

  useLayoutEffect(() => {
    const calculate = () => {
      const container = containerRef.current;

      const measure = measureRef.current;

      if (!container || !measure) {
        return;
      }

      const containerWidth = container.clientWidth;

      const children = Array.from(
        measure.querySelectorAll('[data-item]')
      ) as HTMLElement[];

      const moreButton = measure.querySelector(
        '[data-more]'
      ) as HTMLElement | null;

      if (!moreButton) {
        return;
      }

      let used = 0;
      let visible = items.length;

      for (let i = 0; i < children.length; i++) {
        const itemWidth = children[i].offsetWidth;

        const remaining = measurementItems.length - (i + 1);

        const reserveMore = remaining > 0 ? moreButton.offsetWidth : 0;

        if (used + itemWidth + reserveMore > containerWidth) {
          visible = i;
          break;
        }

        used += itemWidth + GAP;
      }

      setVisibleCount(visible);
    };

    calculate();

    const ro = new ResizeObserver(calculate);

    if (containerRef.current) {
      ro.observe(containerRef.current);
    }

    return () => ro.disconnect();
  }, [items, location.pathname, measurementItems]);

  return (
    <>
      {/* hidden measurement row */}
      <Box
        ref={measureRef}
        sx={{
          position: 'absolute',
          visibility: 'hidden',
          pointerEvents: 'none',
          left: 0,
          top: 0,
          display: 'flex',
          alignItems: 'center',
          gap: `${GAP}px`,
          whiteSpace: 'nowrap',
        }}
      >
        {measurementItems.map((item, index) => (
          <Box
            key={index}
            data-item
            sx={{
              flexShrink: 0,
            }}
          >
            {renderButton(item, index)}
          </Box>
        ))}

        <Box
          data-more
          sx={{
            flexShrink: 0,
          }}
        >
          <Button sx={buttonStyle}>
            <Icon name="KeyboardArrowDownRoundedIcon" />
            +99 more
          </Button>
        </Box>
      </Box>

      {/* visible row */}
      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: `${GAP}px`,
            flexWrap: 'nowrap',
            overflow: 'hidden',
            minWidth: 0,
          }}
        >
          {visibleItems.map(renderButton)}

          {hiddenItems.length > 0 && (
            <>
              <Button
                sx={buttonStyle}
                onClick={(e) => setAnchorEl(e.currentTarget)}
              >
                <Icon name="KeyboardArrowDownRoundedIcon" />+
                {hiddenItems.length} more
              </Button>

              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={() => setAnchorEl(null)}
                disableScrollLock
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {hiddenItems.map((item, index) => {
                  const selected = isSelected(item);

                  return (
                    <MenuItem
                      key={index}
                      sx={{
                        gap: '8px',
                        backgroundColor: selected
                          ? theme.background.lightGrey
                          : undefined,
                      }}
                      onClick={() => {
                        onChangeSubMenu?.(item.path);

                        setAnchorEl(null);
                      }}
                    >
                      {item.icon && (
                        <TabIcon
                          isSelected={selected}
                          name={item.icon}
                          customSizes={{
                            height: '16px',
                            width: '16px',
                          }}
                        />
                      )}

                      {item.label}
                    </MenuItem>
                  );
                })}
              </Menu>
            </>
          )}
        </Box>
      </Box>
    </>
  );
};
